import { FC, useContext, Suspense, useState, ReactNode } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { BasicSelect } from 'dibs-elements/exports/BasicSelect';
import { Button } from 'dibs-elements/exports/Button';
import CurrencyInput from 'dibs-currency-input';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { Link } from 'dibs-elements/exports/Link';
import ArrowLeft from 'dibs-icons/exports/legacy/ArrowLeft';
import { SingleOnChangeArgs } from 'dibs-elements/exports/SingleSelect';
import InputsWrapper from './sharedComponents/InputsWrapper';

import CreatorsSearch from './sharedComponents/CreatorsSearch';
import CategoryL2Select from './sharedComponents/CategoryL2Select';
import ItemCondition from './sharedComponents/ItemCondition';
import ImageUpload from './sharedComponents/ImageUpload';

import SellerFormContext from './SellerFormContext';
import {
    sellerApplicationFormFieldsValidation,
    SellerApplicationFormValidatorValues,
} from '../helpers/sellerApplicationFormValidator';

import { ConsignorItem } from './SellerApplicationFormReducer';
import { SellerApplicationFormConsignorsItemUpload_viewer$key } from './__generated__/SellerApplicationFormConsignorsItemUpload_viewer.graphql';

import dibsCss from 'dibs-css';
import styles from './styles/Spinner.scss';

const messages = defineMessages({
    categoryPlaceholder: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.categoryPlaceholder',
        defaultMessage: 'Item Category',
    },
    itemAcquirePlaceholder: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.itemAcquirePlaceholder',
        defaultMessage: 'How did you acquire this item?',
    },
    itemAcquireOptionNew: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.itemAcquireOptionNew',
        defaultMessage: 'I purchased this item new',
    },
    itemAcquireOptionUsed: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.itemAcquireOptionUsed',
        defaultMessage: 'I purchased this item used',
    },
    itemAcquireOptionGift: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.itemAcquireOptionGift',
        defaultMessage: 'I inherited or was gifted this item',
    },
    pricePlaceholder: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.pricePlaceholder',
        defaultMessage: 'Price',
    },
    retailPricePlaceholder: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.retailPricePlaceholder',
        defaultMessage: 'Retail Price',
    },
    desiredSalePlaceholder: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.desiredSalePlaceholder',
        defaultMessage: 'Desired Sale Price',
    },
    furnitureLabel: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.furnitureLabel',
        defaultMessage: 'Furniture',
    },
    artLabel: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.artLabel',
        defaultMessage: 'Art',
    },
    fashionLabel: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.fashionLabel',
        defaultMessage: 'Fashion',
    },
    jewelryLabel: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.jewelryLabel',
        defaultMessage: 'Jewelry',
    },
});

type ConsignorItemKeys = keyof ConsignorItem;

type ErrorType = { [fieldName in ConsignorItemKeys]?: ReactNode };

const viewerFragment = graphql`
    fragment SellerApplicationFormConsignorsItemUpload_viewer on Viewer {
        ...CategoryL2Select_viewer
        ...ItemCondition_viewer
    }
`;

type Props = {
    viewer: SellerApplicationFormConsignorsItemUpload_viewer$key;
    currentItemIndex: number;
};

const SellerApplicationFormConsignorsItemUpload: FC<Props> = ({
    viewer: viewerRef,
    currentItemIndex,
}) => {
    const intl = useIntl();
    const { state, dispatch } = useContext(SellerFormContext);
    const [errorMessages, setErrorMessages] = useState<ErrorType>({});
    const viewer = useFragment(viewerFragment, viewerRef);
    const { averageItemPriceCurrency: currency } = state.formDataConsignorInventory || {};

    const {
        vertical,
        subcategory,
        condition,
        price,
        howItemWasAcquired,
        imageFiles,
        isAdded,
        creator,
    } = state.consignorItems?.[currentItemIndex] || {};

    const validateField = (
        name: ConsignorItemKeys,
        value: string | boolean | SingleOnChangeArgs | File[]
    ): void => {
        const { errors } = sellerApplicationFormFieldsValidation({ [name]: value });
        const error = errors?.[name];

        setErrorMessages({
            ...errorMessages,
            [name]: error,
        });
    };

    const setFormField = (
        field: ConsignorItemKeys,
        value: string | boolean | SingleOnChangeArgs | File[]
    ): void => {
        validateField(field, value);
        dispatch({
            type: 'SET_CONSIGNOR_ITEM_DATA',
            itemIndex: currentItemIndex,
            item: { [field]: value },
        });
    };

    const setImagesField = (value: File[]): void => {
        setFormField('imageFiles', value);
        validateField('imageFiles', value);
    };

    const onBackClick = (): void => {
        dispatch({
            type: 'SET_STEP',
            step: 'CONSIGNORS_STEP_ITEMS',
        });

        dispatch({
            type: 'REMOVE_CONSIGNOR_ITEM',
        });
    };

    const getPricePlaceholder = (): string => {
        switch (howItemWasAcquired) {
            case 'used': {
                return intl.formatMessage(messages.retailPricePlaceholder);
            }
            case 'gift': {
                return intl.formatMessage(messages.desiredSalePlaceholder);
            }
            default:
                return intl.formatMessage(messages.pricePlaceholder);
        }
    };

    return (
        <div
            className={classnames(
                dibsCss.borderT,
                dibsCss.borderL0,
                dibsCss.borderR0,
                dibsCss.borderB0,
                dibsCss.borderDolphin,
                dibsCss.borderSolid,
                dibsCss.pSmall
            )}
        >
            {!isAdded && (
                <Link
                    onClick={onBackClick}
                    className={classnames(dibsCss.flex, dibsCss.itemsCenter, dibsCss.mbSmall)}
                >
                    <ArrowLeft
                        className={classnames(dibsCss.h14px, dibsCss.w14px, dibsCss.mrXsmall)}
                    />
                    <FormattedMessage
                        id="SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.backButton"
                        defaultMessage="Back"
                    />
                </Link>
            )}
            <InputsWrapper>
                <BasicSelect
                    dataTn="item-category-select"
                    onChange={e => {
                        const value = e.target.value;
                        setFormField('vertical', value);
                    }}
                    placeholder={intl.formatMessage(messages.categoryPlaceholder)}
                    options={[
                        { label: intl.formatMessage(messages.furnitureLabel), value: 'Furniture' },
                        { label: intl.formatMessage(messages.artLabel), value: 'Art' },
                        { label: intl.formatMessage(messages.jewelryLabel), value: 'Jewelry' },
                        { label: intl.formatMessage(messages.fashionLabel), value: 'Fashion' },
                    ]}
                    value={vertical || ''}
                    errorMessage={errorMessages?.vertical}
                    hasError={!!errorMessages?.vertical}
                />
                <Suspense
                    fallback={
                        <div className={styles.spinner}>
                            <Spinner size="tiny" />
                        </div>
                    }
                >
                    <CategoryL2Select
                        viewer={viewer}
                        vertical={vertical}
                        onChange={option => {
                            setFormField('subcategory', option);
                        }}
                        value={subcategory?.value || ''}
                        disabled={!vertical}
                        errorMessage={errorMessages?.subcategory}
                    />
                </Suspense>
            </InputsWrapper>
            <InputsWrapper>
                <CreatorsSearch
                    onChange={option => {
                        dispatch({
                            type: 'SET_CONSIGNOR_ITEM_DATA',
                            itemIndex: currentItemIndex,
                            item: { creator: option?.value ? option : null },
                        });
                    }}
                    vertical={vertical}
                    value={creator}
                />
            </InputsWrapper>
            <InputsWrapper>
                <BasicSelect
                    dataTn="item-acquire-select"
                    onChange={e => {
                        const value = e.target.value;
                        setFormField('howItemWasAcquired', value);
                    }}
                    placeholder={intl.formatMessage(messages.itemAcquirePlaceholder)}
                    options={[
                        {
                            label: intl.formatMessage(messages.itemAcquireOptionNew),
                            value: 'new',
                        },
                        {
                            label: intl.formatMessage(messages.itemAcquireOptionUsed),
                            value: 'used',
                        },
                        {
                            label: intl.formatMessage(messages.itemAcquireOptionGift),
                            value: 'gift',
                        },
                    ]}
                    value={howItemWasAcquired}
                    errorMessage={errorMessages?.howItemWasAcquired}
                    hasError={!!errorMessages?.howItemWasAcquired}
                />
                <CurrencyInput
                    dataTn="price-input"
                    placeholder={getPricePlaceholder()}
                    value={price}
                    onChange={(value: string) => setFormField('price', value)}
                    currency={currency}
                    size="large"
                    errorMessage={errorMessages?.price}
                    hasError={!!errorMessages?.price}
                />
            </InputsWrapper>
            <InputsWrapper>
                <Suspense
                    fallback={
                        <div className={styles.spinner}>
                            <Spinner size="tiny" />
                        </div>
                    }
                >
                    <ItemCondition
                        viewer={viewer}
                        vertical={vertical}
                        value={condition}
                        onChange={e => {
                            const value = e?.value || '';
                            setFormField('condition', value);
                        }}
                        disabled={!vertical}
                        errorMessage={errorMessages?.condition}
                    />
                </Suspense>
            </InputsWrapper>
            <ImageUpload
                imageFiles={imageFiles}
                fieldRequiredErrorMessage={errorMessages?.imageFiles}
                setFormField={setImagesField}
            />
            <Button
                fullWidth
                onClick={() => {
                    const fields: SellerApplicationFormValidatorValues = {
                        vertical,
                        subcategory,
                        condition,
                        price,
                        howItemWasAcquired,
                        imageFiles,
                    };

                    const { isValid, errors } = sellerApplicationFormFieldsValidation(fields);

                    if (!isValid) {
                        setErrorMessages(errors);
                        return;
                    }

                    dispatch({
                        type: 'SET_CONSIGNOR_ITEM_DATA',
                        itemIndex: currentItemIndex,
                        item: { isAdded: true },
                    });
                    dispatch({
                        type: 'SET_STEP',
                        step: 'CONSIGNORS_STEP_ITEMS',
                    });
                }}
            >
                {isAdded ? (
                    <FormattedMessage
                        id="SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.saveItemCta"
                        defaultMessage="save item"
                    />
                ) : (
                    <FormattedMessage
                        id="SellerApplicationForm.SellerApplicationFormConsignorsItemUpload.addItemCta"
                        defaultMessage="add item"
                    />
                )}
            </Button>
        </div>
    );
};

export default SellerApplicationFormConsignorsItemUpload;
